import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Check, Close, Search, Store } from "@material-ui/icons"
import { useSnackbar } from "notistack"
import { useEffect, useMemo, useState } from "react"
import { RegistrarLogErro } from "../../services/clientefiel"
import { buscarEstabelecimentosPaginado, parametrosId } from "../../services/integracao"
import useAppStore from "../../stores/AppStore"
import useLoadingStore from "../../stores/LoadingStore"
import useDebounce from "../../utils/useDebounce"

const useStyles = makeStyles(theme => ({
    listaContainer: {
        position: "relative",
        minHeight: 280,
        maxHeight: 420,
        overflowY: "auto",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 4,
    },
    overlay: {
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(255,255,255,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1,
    },
    item: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    itemAtual: {
        backgroundColor: "#e8f5e9",
        "&:hover": { backgroundColor: "#e8f5e9" },
        cursor: "default",
    },
    itemInativo: {
        opacity: 0.6,
    },
    iconeLoja: {
        color: theme.palette.text.secondary,
        marginRight: 8,
    },
}))

const PAGE_SIZE = 20

export default function DialogTrocaEstabelecimento({ open, onClose }) {
    const classes = useStyles()
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down("xs"))
    const { enqueueSnackbar } = useSnackbar()

    const autorizacao = localStorage.getItem("chaveSistema")
    const configs = useAppStore(s => s.configuracoes)
    const atualizarConfiguracoes = useAppStore(s => s.atualizarConfiguracoes)
    const setLoadingGlobal = useLoadingStore(s => s.setLoading)

    const idAtual = configs?.user?.parametros?.estabelecimento?.id

    const [q, setQ] = useState("")
    const [codigo, setCodigo] = useState("")
    const [apenasAtivos, setApenasAtivos] = useState(true)
    const [page, setPage] = useState(1)

    const qDebounced = useDebounce(q, 300)
    const codigoDebounced = useDebounce(codigo, 300)

    const [estabelecimentos, setEstabelecimentos] = useState([])
    const [total, setTotal] = useState(0)
    const [loading, setLoading] = useState(false)
    const [erro, setErro] = useState("")
    const [aplicando, setAplicando] = useState(false)

    const totalPaginas = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])

    const buscar = async (pageAtual = page) => {
        setLoading(true)
        setErro("")
        const resp = await buscarEstabelecimentosPaginado(
            {
                q: qDebounced,
                codigo: codigoDebounced,
                apenasAtivos,
                page: pageAtual,
                size: PAGE_SIZE,
            },
            autorizacao
        )
        setLoading(false)
        if (resp.retornoErro) {
            setErro(resp.mensagem || "Erro ao buscar estabelecimentos.")
            setEstabelecimentos([])
            setTotal(0)
            return
        }
        setEstabelecimentos(resp.estabelecimentos)
        setTotal(resp.total)
    }

    useEffect(() => {
        if (open) {
            setPage(1)
            buscar(1)
        } else {
            setQ("")
            setCodigo("")
            setApenasAtivos(true)
            setPage(1)
            setEstabelecimentos([])
            setTotal(0)
            setErro("")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
        if (!open) return
        setPage(1)
        buscar(1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qDebounced, codigoDebounced, apenasAtivos])

    const handleTrocarPagina = nova => {
        setPage(nova)
        buscar(nova)
    }

    const selecionar = async estab => {
        if (estab.id === idAtual || aplicando) return
        setAplicando(true)
        setLoadingGlobal(true)
        try {
            const resp = await parametrosId(estab.id, autorizacao)
            if (resp?.retornoErro) {
                enqueueSnackbar(resp.mensagem || "Erro ao trocar de estabelecimento.", {
                    variant: "error",
                })
                return
            }

            const parametrosEnriquecidos = {
                ...resp,
                estabelecimento: {
                    ...resp?.estabelecimento,
                    codigo: estab.codigo,
                    nomeAlternativo: estab.nomeAlternativo,
                    nomeRede: estab.nomeRede,
                },
            }
            const novasConfigs = {
                ...configs,
                user: { ...configs.user, parametros: parametrosEnriquecidos },
            }
            atualizarConfiguracoes(novasConfigs)

            const estabAtualParaPersistir = {
                id: estab.id,
                nome: estab.nome,
                nomeAlternativo: estab.nomeAlternativo,
                codigo: estab.codigo,
                ativo: estab.ativo,
                idRede: estab.idRede,
                nomeRede: estab.nomeRede,
                urlLogo: resp?.estabelecimento?.urlLogo,
            }
            localStorage.setItem("estabelecimentoAtualCF", JSON.stringify(estabAtualParaPersistir))
            localStorage.setItem("configs", JSON.stringify(novasConfigs))

            enqueueSnackbar(`Você está agora no estabelecimento ${estab.nome}`, {
                variant: "success",
            })

            setTimeout(() => window.location.reload(), 400)
        } catch (error) {
            RegistrarLogErro(error, "DialogTrocaEstabelecimento.selecionar", {
                idEstabelecimento: estab.id,
            })
            enqueueSnackbar("Erro inesperado ao trocar de estabelecimento.", { variant: "error" })
        } finally {
            setAplicando(false)
            setLoadingGlobal(false)
        }
    }

    const inicio = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
    const fim = total === 0 ? 0 : Math.min(total, (page - 1) * PAGE_SIZE + estabelecimentos.length)

    return (
        <Dialog
            open={open}
            onClose={() => !aplicando && onClose?.()}
            fullScreen={fullScreen}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle disableTypography>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">Trocar Estabelecimento</Typography>
                    <IconButton size="small" onClick={onClose} disabled={aplicando}>
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Buscar por nome"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Código"
                            value={codigo}
                            onChange={e => setCodigo(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!apenasAtivos}
                                    onChange={e => setApenasAtivos(!e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Incluir inativos"
                        />
                    </Grid>
                </Grid>

                <Box className={classes.listaContainer} mt={1}>
                    {loading && (
                        <Box className={classes.overlay}>
                            <CircularProgress size={32} />
                        </Box>
                    )}
                    {!loading && erro && (
                        <Paper
                            elevation={0}
                            style={{
                                padding: 16,
                                margin: 16,
                                backgroundColor: "#fdecea",
                                border: "1px solid #f5c2c0",
                            }}
                        >
                            <Typography variant="body2" style={{ color: "#611a15" }}>
                                {erro}
                            </Typography>
                            <Box mt={1}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => buscar(page)}
                                >
                                    Tentar novamente
                                </Button>
                            </Box>
                        </Paper>
                    )}
                    {!loading && !erro && estabelecimentos.length === 0 && (
                        <Box p={3}>
                            <Typography variant="body2" color="textSecondary" align="center">
                                Nenhum estabelecimento encontrado para esta busca.
                            </Typography>
                        </Box>
                    )}
                    {!erro && estabelecimentos.length > 0 && (
                        <List dense disablePadding>
                            {estabelecimentos.map(est => {
                                const ehAtual = est.id === idAtual
                                const linhaCls = `${classes.item} ${
                                    ehAtual ? classes.itemAtual : ""
                                } ${!est.ativo ? classes.itemInativo : ""}`
                                const subtitulo = est.nomeRede || ""
                                return (
                                    <ListItem
                                        key={est.id}
                                        className={linhaCls}
                                        onClick={() => selecionar(est)}
                                    >
                                        <Store className={classes.iconeLoja} />
                                        <ListItemText
                                            primary={
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{ gap: 8 }}
                                                >
                                                    {est.id != null && (
                                                        <Chip
                                                            size="small"
                                                            label={est.id}
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    <span>{est.nome}</span>
                                                    {est.ativo === false && (
                                                        <Chip
                                                            size="small"
                                                            label="Inativo"
                                                            style={{
                                                                backgroundColor: "#9e9e9e",
                                                                color: "white",
                                                            }}
                                                        />
                                                    )}
                                                    {ehAtual && (
                                                        <Chip
                                                            size="small"
                                                            icon={
                                                                <Check style={{ color: "white" }} />
                                                            }
                                                            label="Atual"
                                                            style={{
                                                                backgroundColor: "#28a745",
                                                                color: "white",
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={subtitulo}
                                        />
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}
                </Box>

                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    mt={1}
                    style={{ gap: 8 }}
                >
                    <Typography variant="caption" color="textSecondary">
                        {total === 0 ? "—" : `Exibindo ${inicio}–${fim} de ${total}`}
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={page <= 1 || loading}
                            onClick={() => handleTrocarPagina(page - 1)}
                        >
                            Anterior
                        </Button>
                        <Typography variant="body2">
                            Página {page} de {totalPaginas}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={page >= totalPaginas || loading}
                            onClick={() => handleTrocarPagina(page + 1)}
                        >
                            Próximo
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={aplicando}>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
